import Parse from 'parse';
import { Observable, of, throwError } from "rxjs";
import { fromPromise } from "rxjs/internal-compatibility";
import { IotDevicesParse } from "./Iotdevices.Parse";
import {
    arrayIsSet, deepClone,
    paramsParseQuery,
    stringIsSet
} from "../providers/service/global-variables.service";
import { PidConfiguration } from "./pid_configurations";

export class BigBuddyPinWrapper {
    // Usata anche nel Frontend per mostrare le opzioni disponibili
    static possiblePins: number[] = [1, 2, 3, 4, 5, 6, 7, 10, 11, 12, 13, 14, 15, 16];

    // Usata anche nel Frontend per mostrare le opzioni disponibili
    static possibleFunctionModesPerPin: { [k: number]: number[] } = {
        1: [0],
        2: [0],
        3: [0],
        4: [0, 1],
        5: [0, 1],
        6: [0, 1, 2],
        7: [0, 1, 2],
        10: [0],
        11: [0],
        12: [0],
        13: [0],
        14: [0],
        15: [0],
        16: [1]
    };

    static functionModesNamePerMode: { [k: number]: string } = {
        0: "Digital",
        1: "Analog 0-10V",
        2: "Analog 4-20mA"
    };
    static functionModesNamePerModePossibleValues = Object.values(BigBuddyPinWrapper.functionModesNamePerMode)

    static getFunctionModeNamePerModeByValue(value: number): string | undefined {
        return BigBuddyPinWrapper.functionModesNamePerMode[value];
    }

    /** Restituisce il valore numerico dato il nome */
    static getFunctionModeValueByName(name: string): number | undefined {
        if (name == null) {
            return
        }
        if (name && BigBuddyPinWrapper.functionModesNamePerMode[parseInt(name)] != null) {
            return parseInt(name)
        }
        const entry = Object.entries(BigBuddyPinWrapper.functionModesNamePerMode)
            .find(([_, v]) => v.toLowerCase() === name.toLowerCase());
        return entry ? Number(entry[0]) : undefined;
    }

    static getFunctionModeForPin(pin: number | undefined): { value: number, traduction: string }[] | undefined {
        if (pin == null) {
            return undefined
        }
        const values = this.possibleFunctionModesPerPin[pin]
        if (values) {
            return values.map(v => {
                return { value: v, traduction: this.functionModesNamePerMode[v] }
            })
        } else {
            return undefined
        }
    }

    // Usata anche nel Frontend per mostrare le opzioni disponibili
    static getIsInput(pin: number | null): boolean | null {
        if (pin == null) {
            return pin
        }
        const PIN_MAP: { [k: number]: boolean } = {
            1: true,
            2: true,
            3: true,
            4: true,
            5: true,
            6: true,
            7: true,
            10: false,
            11: false,
            12: false,
            13: false,
            14: false,
            15: false,
            16: false
        };
        return PIN_MAP[pin];
    }
}


export class PicoBuddyPinWrapper {
    // Usata anche nel Frontend per mostrare le opzioni disponibili
    static possiblePins: number[] = [1, 2, 3, 4, 5, 6, 7, 10, 11, 12, 13, 14, 15, 16];
    static possibleImpulseInputPins: number[] = [1, 2, 3, 4, 5, 6, 7];

    // Usata anche nel Frontend per mostrare le opzioni disponibili
    static possibleFunctionModesPerPin: { [k: number]: number[] } = {
        1: [0],
        2: [0],
        3: [0],
        4: [0],
        5: [0, 1],
        6: [0, 1],
        7: [0, 1, 2],
        10: [0],
        11: [0],
        12: [0],
        13: [0],
        14: [0],
        15: [0],
        16: [1]
    };

    static functionModesNamePerMode: { [k: number]: string } = {
        0: "Digital",
        1: "Analog 0-10V",
        2: "Analog 4-20mA"
    };

    static getAuxPinFromFunctionMode(functionMode: number | null, pin: number): number | null {
        switch (functionMode) {
            case 0:
            case 1:
            case 2:
                if (pin === 7) return 7;  // Pin fisico 7 -> Aux Pin fisico 7
                return null;
            default:
                return null;
        }
    }

    static getIsDigitalFromFunctionMode(functionMode: number | null): boolean {
        switch (functionMode) {
            case 1:
                return false
            case 2:
                return false
            default:
                return true;
        }
    }

    // Usata anche nel Frontend per mostrare le opzioni disponibili
    static getIsInput(pin: number | null): boolean | null {
        if (pin == null) return null;
        const PIN_MAP: { [k: number]: boolean } = {
            1: true,
            2: true,
            3: true,
            4: true,
            5: true,
            6: true,
            7: true,
            10: false,
            11: false,
            12: false,
            13: false,
            14: false,
            15: false,
            16: false
        };
        return PIN_MAP[pin];
    }

    static wrapPin(pin: number | null): number | null {
        // Mappa di 13 pin logici -> pin fisici (assunzione: mapping tipo BCM Raspberry Pi)
        // Se necessario si potrà estendere/variare; i valori non trovati restituiscono l'input originale
        if (pin == null) return null;
        const PIN_MAP: { [k: number]: number } = {
            1: 3,
            2: 4,
            3: 5,
            4: 6,
            5: 26,
            6: 27,
            7: 28,
            10: 7,
            11: 8,
            12: 9,
            13: 10,
            14: 11,
            15: 12,
            16: 13
        };
        return PIN_MAP[pin] ?? pin;
    }

    static getAuxPin(pin: number | null): number | null {
        if (pin == null) return null;
        const PIN_MAP: { [k: number]: number } = {
            7: 7
        };
        return PIN_MAP[pin] ?? pin;
    }

    static getInverted(pin: number | null): boolean | null {
        if (pin == null) return false;
        const PIN_MAP: { [k: number]: boolean } = {
            1: true,
            2: true,
            3: true,
            4: true,
            5: true,
            6: true,
            7: true,
            10: false,
            11: false,
            12: false,
            13: false,
            14: false,
            15: false,
            16: false
        };
        return PIN_MAP[pin] ?? false;
    }
    static getFunctionModeForPin(pin: number | undefined): { value: number, traduction: string }[] | undefined {
        if (pin == null) {
            return undefined
        }
        const values = this.possibleFunctionModesPerPin[pin]
        if (values) {
            return values.map(v => {
                return { value: v, traduction: this.functionModesNamePerMode[v] }
            })
        } else {
            return undefined
        }
    }
}

export enum ModalityType {
    NONE = -1,
    CLEANING = 0,
    BOOST = 1
}

export enum ComTypeEnum {
    MODBUS_RTU,
    HTTP,
    MQTT,
    Z_WAVE,
    GPIO,
    VIRTUAL
}

export type Modality = {
    type: ModalityType;
    value: number;
    expirationDate: Date;
}

export type ModbusOptions = {
    baudRate: number;
    parity: string;
    stop_bits: number;
    data_bits: number;

}

// randomUUID is only available in secure contexts (https/localhost)
function generateConfigurationId(key: string): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return key + '_' + crypto.randomUUID();
    }
    return Date.now().toString(32) + Math.random().toString(26).slice(2, 10);
}

export enum ExternalInfoType {
    RECORD_SETPOINT = 0,
    RECORD_WEATHER_TEMPERATURE,
    FROM,
    MEAN,
    MAX,
    MIN,
    SUM,
    DIFF,
    MOVING_MEAN,
    VARIATION,
    TIME_DERIVATE
}

export class ExternalInfoParse {
    positionIndex?: number // usato nel FE
    sensorId?: string;
    infoName?: string;
    recordSetpoint?: number;
    recordWeatherTemperature?: number;
    mean?: {
        sensorIds: string[],
        infoName: string
    }
    max?: {
        sensorIds: string[],
        infoName: string
    }
    min?: {
        sensorIds: string[],
        infoName: string
    }
    sum?: {
        sensorIds: string[],
        infoName: string
    }
    diff?: { sensorIdsSum: string[], sensorIdsDiff: string[], infoName: string }
    movingMean?: { sensorId: string, infoName: string, minutes: number }
    variation?: { sensorId: string, infoName: string }
    timeDerivate?: { sensorId: string, infoName: string }

    get type() {
        if (this.recordSetpoint != null) {
            return ExternalInfoType.RECORD_SETPOINT
        } else if (this.recordWeatherTemperature != null) {
            return ExternalInfoType.RECORD_WEATHER_TEMPERATURE
        } else if (this.sensorId != null && this.infoName != null) {
            return ExternalInfoType.FROM
        } else if (this.mean) {
            return ExternalInfoType.MEAN
        } else if (this.max) {
            return ExternalInfoType.MAX
        } else if (this.min) {
            return ExternalInfoType.MIN
        } else if (this.sum) {
            return ExternalInfoType.SUM
        } else if (this.diff) {
            return ExternalInfoType.DIFF
        } else if (this.movingMean) {
            return ExternalInfoType.MOVING_MEAN
        } else if (this.variation) {
            return ExternalInfoType.VARIATION
        } else if (this.timeDerivate) {
            return ExternalInfoType.TIME_DERIVATE
        } else {
            return ExternalInfoType.FROM
        }

    }

    static getType(json: any): ExternalInfoType {
        if (!json) return ExternalInfoType.FROM;
        if (json.recordSetpoint != null) return ExternalInfoType.RECORD_SETPOINT;
        if (json.recordWeatherTemperature != null) return ExternalInfoType.RECORD_WEATHER_TEMPERATURE;
        if (json.mean) return ExternalInfoType.MEAN;
        if (json.max) return ExternalInfoType.MAX;
        if (json.min) return ExternalInfoType.MIN;
        if (json.sum) return ExternalInfoType.SUM;
        if (json.diff) return ExternalInfoType.DIFF;
        if (json.movingMean) return ExternalInfoType.MOVING_MEAN;
        if (json.variation) return ExternalInfoType.VARIATION;
        if (json.timeDerivate) return ExternalInfoType.TIME_DERIVATE;
        if (json.sensorId != null && json.infoName != null) return ExternalInfoType.FROM;
        return ExternalInfoType.FROM;
    }

    get currentInfoName() {
        let infoName: string | undefined
        if (this.type == ExternalInfoType.FROM) {
            if (this.infoName) {
                infoName = this.infoName
            }
        } else if (this.type == ExternalInfoType.MIN) {
            if (stringIsSet(this.min?.infoName)) {
                infoName = this.min?.infoName
            }
        } else if (this.type == ExternalInfoType.MAX) {
            if (stringIsSet(this.max?.infoName)) {
                infoName = this.max?.infoName
            }
        } else if (this.type == ExternalInfoType.MEAN) {
            if (stringIsSet(this.mean?.infoName)) {
                infoName = this.mean?.infoName
            }
        } else if (this.type == ExternalInfoType.DIFF) {
            if (stringIsSet(this.diff?.infoName)) {
                infoName = this.diff?.infoName
            }

        } else if (this.type == ExternalInfoType.MOVING_MEAN) {
            if (stringIsSet(this.movingMean?.infoName)) {
                infoName = this.movingMean?.infoName
            }
        } else if (this.type == ExternalInfoType.VARIATION) {
            if (stringIsSet(this.variation?.infoName)) {
                infoName = this.variation?.infoName
            }
        } else if (this.type == ExternalInfoType.TIME_DERIVATE) {
            if (stringIsSet(this.timeDerivate?.infoName)) {
                infoName = this.timeDerivate?.infoName
            }
        }
        return infoName
    }

    get allSensorIds() {
        let sensors: string[] = []

        if (this.type == ExternalInfoType.FROM) {
            if (this.sensorId) {
                sensors = [this.sensorId]
            }
        } else if (this.type == ExternalInfoType.MIN) {
            if (arrayIsSet(this.min?.sensorIds)) {
                sensors = this.min?.sensorIds
            }
        } else if (this.type == ExternalInfoType.MAX) {
            if (arrayIsSet(this.max?.sensorIds)) {
                sensors = this.max?.sensorIds
            }
        } else if (this.type == ExternalInfoType.MEAN) {
            if (arrayIsSet(this.mean?.sensorIds)) {
                sensors = this.mean?.sensorIds
            }
        } else if (this.type == ExternalInfoType.DIFF) {
            if (arrayIsSet(this.diff?.sensorIdsDiff)) {
                sensors = sensors.concat(this.diff?.sensorIdsDiff)
            }
            if (arrayIsSet(this.diff?.sensorIdsSum)) {
                sensors = sensors.concat(this.diff?.sensorIdsDiff)
            }

        } else if (this.type == ExternalInfoType.MOVING_MEAN) {
            if (this.movingMean?.sensorId) {
                sensors = [this.movingMean?.sensorId]
            }
        } else if (this.type == ExternalInfoType.VARIATION) {
            if (this.variation?.sensorId) {
                sensors = [this.variation?.sensorId]
            }
        } else if (this.type == ExternalInfoType.TIME_DERIVATE) {
            if (this.timeDerivate?.sensorId) {
                sensors = [this.timeDerivate?.sensorId]
            }
        }
        return sensors

    }

    static fromJson(json: any): ExternalInfoParse {
        const instance = new ExternalInfoParse();

        if (!json || typeof json !== 'object') return instance;

        // Copia diretta delle chiavi semplici
        instance.sensorId = json.sensorId ?? undefined;
        instance.infoName = json.infoName ?? undefined;
        instance.recordSetpoint = json.recordSetpoint != null ? json.recordSetpoint : undefined;
        instance.recordWeatherTemperature = json.recordWeatherTemperature != null ? json.recordWeatherTemperature : undefined;

        // Individua il tipo (può dipendere dalle chiavi presenti)
        const type = ExternalInfoParse.getType(json);

        // Ricostruisce i sotto-oggetti in base al tipo
        switch (type) {
            case ExternalInfoType.MEAN:
                instance.mean = {
                    sensorIds: json.mean?.sensorIds ?? [],
                    infoName: json.mean?.infoName ?? json.infoName ?? ''
                };
                break;

            case ExternalInfoType.MAX:
                instance.max = {
                    sensorIds: json.max?.sensorIds ?? [],
                    infoName: json.max?.infoName ?? json.infoName ?? ''
                };
                break;

            case ExternalInfoType.MIN:
                instance.min = {
                    sensorIds: json.min?.sensorIds ?? [],
                    infoName: json.min?.infoName ?? json.infoName ?? ''
                };
                break;

            case ExternalInfoType.SUM:
                instance.sum = {
                    sensorIds: json.sum?.sensorIds ?? [],
                    infoName: json.sum?.infoName ?? json.infoName ?? ''
                };
                break;

            case ExternalInfoType.DIFF:
                instance.diff = {
                    sensorIdsSum: json.diff?.sensorIdsSum ?? [],
                    sensorIdsDiff: json.diff?.sensorIdsDiff ?? [],
                    infoName: json.diff?.infoName ?? json.infoName ?? ''
                };
                break;

            case ExternalInfoType.MOVING_MEAN:
                instance.movingMean = {
                    sensorId: json.movingMean?.sensorId ?? '',
                    infoName: json.movingMean?.infoName ?? '',
                    minutes: json.movingMean?.minutes ?? 0
                };
                break;
            case ExternalInfoType.VARIATION:
                instance.variation = {
                    sensorId: json.variation?.sensorId ?? '',
                    infoName: json.variation?.infoName ?? '',
                };
                break;
            case ExternalInfoType.TIME_DERIVATE:
                instance.timeDerivate = {
                    sensorId: json.timeDerivate?.sensorId ?? '',
                    infoName: json.timeDerivate?.infoName ?? '',
                };
                break;
        }
        return instance;
    }

    toJson(): any {
        const json: any = {};

        // Copia diretta delle chiavi semplici
        if (this.sensorId !== undefined) json.sensorId = this.sensorId;
        if (this.infoName !== undefined) json.infoName = this.infoName;
        if (this.recordSetpoint != null) json.recordSetpoint = this.recordSetpoint;
        if (this.recordWeatherTemperature != null)
            json.recordWeatherTemperature = this.recordWeatherTemperature;

        // Usa il tipo per decidere quale sotto-oggetto serializzare
        switch (this.type) {
            case ExternalInfoType.MEAN:
                json.mean = {
                    sensorIds: this.mean?.sensorIds ?? [],
                    infoName: this.mean?.infoName ?? this.infoName ?? ''
                };
                break;

            case ExternalInfoType.MAX:
                json.max = {
                    sensorIds: this.max?.sensorIds ?? [],
                    infoName: this.max?.infoName ?? this.infoName ?? ''
                };
                break;

            case ExternalInfoType.MIN:
                json.min = {
                    sensorIds: this.min?.sensorIds ?? [],
                    infoName: this.min?.infoName ?? this.infoName ?? ''
                };
                break;

            case ExternalInfoType.SUM:
                json.sum = {
                    sensorIds: this.sum?.sensorIds ?? [],
                    infoName: this.sum?.infoName ?? this.infoName ?? ''
                };
                break;

            case ExternalInfoType.DIFF:
                json.diff = {
                    sensorIdsSum: this.diff?.sensorIdsSum ?? [],
                    sensorIdsDiff: this.diff?.sensorIdsDiff ?? [],
                    infoName: this.diff?.infoName ?? this.infoName ?? ''
                };
                break;

            case ExternalInfoType.MOVING_MEAN:
                json.movingMean = {
                    sensorId: this.movingMean?.sensorId ?? '',
                    infoName: this.movingMean?.infoName ?? '',
                    minutes: this.movingMean?.minutes ?? 0
                };
                break;
            case ExternalInfoType.VARIATION:
                json.variation = {
                    sensorId: this.variation?.sensorId ?? '',
                    infoName: this.variation?.infoName ?? '',
                };
                break;
            case ExternalInfoType.TIME_DERIVATE:
                json.timeDerivate = {
                    sensorId: this.variation?.sensorId ?? '',
                    infoName: this.variation?.infoName ?? '',
                };
                break;

            case ExternalInfoType.RECORD_SETPOINT:
                json.recordSetpoint = this.recordSetpoint ?? null;
                break;

            default:
                // Se è di tipo FROM o non riconosciuto, mantieni le chiavi base
                break;
        }

        return json;
    }
}

export class MeterConfiguration {
    id: string = generateConfigurationId('meter');
    comType?: ComTypeEnum;
    model?: string;
    mqttTopics?: string[]
    modbusId?: number;
    element?: string;
    initialActiveEnergyValue?: number;
    initialReactiveEnergyValue?: number;
    modbusSerialPort?: string;
    modbusOptions?: ModbusOptions;
    isEnergyProduction?: boolean;
    isNetworkEnergy?: boolean;
    isBattery?: boolean;
    correctionCoefficient?: number;
    skipSend?: boolean;

    static fromJson(json: any): MeterConfiguration {
        const instance = new MeterConfiguration();
        if (!json || typeof json !== 'object') return instance;

        // keeps also the keys not declared in the class
        Object.assign(instance, json);
        if (json.modbusOptions != null) {
            instance.modbusOptions = { ...json.modbusOptions };
        }
        if (Array.isArray(json.mqttTopics)) {
            instance.mqttTopics = [...json.mqttTopics];
        }
        return instance;
    }

    static getInvalidKeys(conf: MeterConfiguration): string[] | undefined {
        const keys = []
        const { element, model, isEnergyProduction, isNetworkEnergy, isBattery, comType } = conf
        if (element == null || element.length == 0) {
            keys.push('element')
        }
        if (model == null || model.length == 0) {
            keys.push('model')
        }


        if (isEnergyProduction == null && isNetworkEnergy == null && isBattery == null) {
            keys.push('isEnergyProduction')
            keys.push('isNetworkEnergy')
            keys.push('isBattery')
        }
        switch (comType) {
            case ComTypeEnum.MODBUS_RTU: {
                const { modbusId } = conf
                if (modbusId == null || modbusId <= 0) {
                    keys.push('modbusId')
                }
                break
            }
            case ComTypeEnum.MQTT: {
                const { mqttTopics } = conf
                if (mqttTopics == null || mqttTopics.length == 0) {
                    keys.push('mqttTopics')
                }
                break
            }

        }
        return arrayIsSet(keys) ? keys : undefined
    }

}

export class MachineConfiguration {
    id: string = generateConfigurationId('machine');
    comType?: ComTypeEnum;
    model?: string;
    element?: string;
    modbusId?: number;
    modbusSerialPort?: string;
    modbusOptions?: ModbusOptions;
    baseUrl?: string;
    password?: string;
    zwNodeId?: number;
    mqttTopics?: string[]
    pin?: number;
    digital?: boolean;
    input?: boolean;
    inverted?: boolean;
    auxPin?: number;
    optionalParameters?: Record<string, any> & { externalInfo?: ExternalInfoParse[] }
    skipSend?: boolean;

    static fromJson(json: any): MachineConfiguration {
        const instance = new MachineConfiguration();
        if (!json || typeof json !== 'object') return instance;

        // keeps also the keys not declared in the class (e.g. s7Host, functionMode)
        Object.assign(instance, json);
        if (json.optionalParameters != null) {
            instance.optionalParameters = { ...json.optionalParameters };
            if (arrayIsSet(json.optionalParameters.externalInfo)) {
                instance.optionalParameters!.externalInfo = json.optionalParameters.externalInfo.map((e: any) => ExternalInfoParse.fromJson(e));
            }
        }
        return instance;
    }


    static getInvalidKeys(conf: MachineConfiguration): string[] | undefined {
        const { element, model } = conf
        const keys = []
        if (element == null || element.length == 0) {
            keys.push('element')
        }
        if (model == null) {
            keys.push('model')
        }
        switch (conf.comType) {
            case ComTypeEnum.MODBUS_RTU: {
                const { modbusId } = conf
                if (modbusId == null || modbusId <= 0) {
                    keys.push('modbusId')
                }
                break;

            }
            case ComTypeEnum.HTTP: {
                const { baseUrl } = conf
                if (baseUrl == null || baseUrl.length == 0) {
                    keys.push('baseUrl')
                }
                break;

            }
            case ComTypeEnum.MQTT: {
                const { mqttTopics } = conf
                if (mqttTopics == null || mqttTopics.length == 0) {
                    keys.push('mqttTopics')
                }
                break;
            }
            case ComTypeEnum.Z_WAVE: {
                const { zwNodeId } = conf
                if (zwNodeId == null || zwNodeId < 0) {
                    keys.push('zwNodeId')
                }
                break;

            }
            case ComTypeEnum.GPIO: {
                const { pin, digital, input } = conf
                if (pin == null) {
                    keys.push('pin')
                }
                // if (digital == null) {
                //     keys.push('digital')
                // }
                // if (input == null) {
                //     keys.push('input')
                // }
                break;

            }

        }
        return arrayIsSet(keys) ? keys : undefined
    }
}

export type ConnectionOptionsConfiguration = {
    zwSerialPort: string;
    mqttEnabled: boolean;
    mqttUsername: string;
    mqttPassword: string;
}

export type IoTDeviceConfigurationObject = {
    meters: MeterConfiguration[];
    machines: MachineConfiguration[];
    connectionOptions: ConnectionOptionsConfiguration;
}

export class IoTDeviceConfigurationParse2 {
    public id?: string;
    public localId?: string;
    public _creation_date?: Date;
    public _last_edit_date?: Date;
    public _pid_configurations?: PidConfiguration[];
    public _device?: IotDevicesParse;
    public _configuration?: IoTDeviceConfigurationObject;
    public _ricevuto?: boolean;
    /**
     * @deprecated has been moved in HouseConfiguration
     */
    public _modality?: ModalityType;
    /**
     * @deprecated has been moved in HouseConfiguration
     */
    /**
     * @deprecated has been moved in HouseConfiguration
     */
    public _modalityExpirationDate?: Date;

    constructor(json: any) {
        this.fromJson(json)
        // @ts-ignore
        if (!stringIsSet(this.id)) {
            this.localId = generateConfigurationId('config');
        }
    }

    public get objectId(): string {
        if (stringIsSet(this.id)) {
            return this.id;
        } else {
            return this.localId as string;
        }
    }

    public set objectId(value) {
        this.id = value;
    }

    public get creation_date(): Date | undefined {
        return this._creation_date
    }

    public set creation_date(value) {
        this._creation_date = value;
    }

    public get last_edit_date(): Date | undefined {
        return this._last_edit_date
    }

    public set last_edit_date(value) {
        this._last_edit_date = value;
    }

    public get pid_configurations(): PidConfiguration[] | undefined {
        return this._pid_configurations
    }

    public set pid_configurations(value) {
        this._pid_configurations = value;
    }

    public get configuration(): IoTDeviceConfigurationObject | undefined {
        return this._configuration
    }

    public set configuration(value) {
        this._configuration = value;
    }

    public get device(): IotDevicesParse | undefined {
        return this._device
    }

    public set device(value) {
        this._device = value;
    }

    /**
     * @deprecated
     */
    public get modality(): ModalityType | undefined {
        return this._modality
    }

    public set modality(value) {
        this._modality = value;
    }

    /**
     * @deprecated
     */
    public get modalityExpirationDate(): Date | undefined {
        return this._modalityExpirationDate
    }

    public set modalityExpirationDate(value) {
        this._modalityExpirationDate = value;
    }

    public get ricevuto(): boolean | undefined {
        return this._ricevuto
    }

    public set ricevuto(value) {
        this._ricevuto = value;
    }


    unset(key: string) {
        if (key in this) {
            (this as any)[key] = undefined
        }
    }

    fromJson(json: any) {
        if (json != null) {
            if (stringIsSet(json.objectId)) {
                this.objectId = json.objectId
            } else if (stringIsSet(json.id)) {
                this.objectId = json.id;
            }
            if (json.device != null) {
                const device = new IotDevicesParse().fromJson(json.device)
                this.device = device
            }
            if (json.pid_configurations != null && arrayIsSet(json.pid_configurations)) {
                const pid_configurations = json.pid_configurations.map((pid_configurations: any) => new PidConfiguration(pid_configurations))
                this.pid_configurations = pid_configurations
            }
            if (json.configuration != null) {
                const configuration = deepClone(json.configuration)
                if (arrayIsSet(configuration.machines)) {
                    configuration.machines = configuration.machines.map((machine: any) => MachineConfiguration.fromJson(machine))
                }
                if (arrayIsSet(configuration.meters)) {
                    configuration.meters = configuration.meters.map((meter: any) => MeterConfiguration.fromJson(meter))
                }
                this.configuration = configuration
            }
            const specialKeys = ['objectId', 'id', 'device', 'pid_configurations', 'configuration',]
            Object.keys(json)
                .forEach(key => {
                    if (!specialKeys.includes(key)) {
                        (this as any)[key] = json[key];
                    }
                })
        }
        return this;
    }


    setConfiguration$(progettoId: string, configuration: IoTDeviceConfigurationObject, serial: string | undefined): Observable<boolean> {
        if (configuration != null) {
            const requestParams: Record<string, any> = paramsParseQuery({
                progettoId,
                serial,
                configuration
            })
            return (fromPromise(Parse.Cloud.run('setIoTDeviceConfigurationObject_v2', requestParams)) as Observable<any>)
        } else {
            return throwError('JSON not contain accepted value')
        }
    }

}






